import type { JsonFetchOptions } from '../types'

import { isResponseError } from '../predicates'

import humps from 'humps'

import { camelizeJSON, parse } from './common'
import { raw } from './raw'

const parseJSON = <R>(response: Response): Promise<R> =>
  response.json() as Promise<R>

const decamelizeBody = (body: Record<string, unknown>): BodyInit => {
  const decamelizedBody = humps.decamelizeKeys(body)
  return JSON.stringify(decamelizedBody)
}

export const json = async <R>(
  url: URL | RequestInfo,
  options: JsonFetchOptions = {},
  preferences: { camelizeJSON: boolean } = { camelizeJSON: true }
): Promise<R | null> => {
  options.headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...options.headers,
  }

  const effectiveOptions = options.body
    ? {
        ...options,
        body: decamelizeBody(options.body),
      }
    : (options as RequestInit)

  const data = await raw(url, effectiveOptions)

  const parsedData = await parse<R>(parseJSON)(data)

  if (!parsedData) {
    return null
  }

  return preferences.camelizeJSON
    ? (camelizeJSON(parsedData) as R)
    : (parsedData as R)
}

export const jsonWithAlert = async <T>(
  url: URL | RequestInfo,
  options: JsonFetchOptions = {},
  preferences: { camelizeJSON: boolean } = { camelizeJSON: true }
): Promise<T | null | void> => {
  try {
    return await json<T>(url, options, preferences)
  } catch (e) {
    // We don't want to show alert for Browser error like cancelled requests
    if (isResponseError(e) && typeof e.body !== 'string' && e.status !== 0) {
      const errorMessage = Object.values(e?.body?.errors || []).join()
      if (errorMessage) {
        alert('Something went wrong: ' + errorMessage)
      }
    }
  }
}
